import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    BookOpen,
    Plus,
    RefreshCw,
    Trash2,
    Edit3,
    X,
    Search,
    ExternalLink,
    Tag,
    Download,
    AlertTriangle,
    Activity,
} from "lucide-react";

const KIND_META = {
    regulatory: { label: "Regulatory", tone: "text-amber-300 border-amber-300/30 bg-amber-300/5" },
    prompt_template: { label: "Prompt Template", tone: "text-[#c7a6ff] border-[#7C3AED]/30 bg-[#7C3AED]/10" },
    sop: { label: "SOP", tone: "text-emerald-300 border-emerald-300/30 bg-emerald-300/5" },
    reference: { label: "Reference", tone: "text-sky-300 border-sky-300/30 bg-sky-300/5" },
};
const KIND_OPTIONS = ["regulatory", "prompt_template", "sop", "reference"];

function fmt(ts) { try { return ts ? new Date(ts).toLocaleString() : "—"; } catch { return String(ts); } }
function formatErr(detail) {
    if (!detail) return "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
}

export default function CommonKnowledgePage() {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ q: "", kind: "" });
    const [editor, setEditor] = useState(null); // null | "new" | item
    // The ingestion-curation flow piggy-backs on the same modal layer:
    //   null              → no modal
    //   { ck, preview }   → curation modal showing the freshly-fetched
    //                       source side-by-side with the editable
    //                       curated version
    const [curating, setCurating] = useState(null);
    const [rechecking, setRechecking] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const q = new URLSearchParams();
            Object.entries(filters).forEach(([k, v]) => v && q.set(k, v));
            q.set("limit", "200");
            const { data } = await api.get(`/admin/common-knowledge?${q.toString()}`);
            setItems(data.items || []);
            setCounts(data.counts || {});
        } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, []);

    const remove = async (item) => {
        if (!window.confirm(`Delete "${item.title}"? This is irreversible.`)) return;
        try {
            await api.delete(`/admin/common-knowledge/${item.id}`);
            toast.success("Deleted");
            load();
        } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail));
        }
    };

    const ingestSource = async (item) => {
        if (!item.source_url) {
            toast.error("This entry has no source URL — add one first.");
            return;
        }
        const t = toast.loading(`Fetching ${new URL(item.source_url).hostname}…`);
        try {
            const { data } = await api.post(
                `/admin/common-knowledge/${item.id}/ingest-source`,
                {},
                { timeout: 60000 },
            );
            toast.success(
                data.unchanged
                    ? `Source unchanged since your last curation (${data.bytes} bytes).`
                    : `Source fetched — ${data.bytes} bytes, ${data.preview_text.length.toLocaleString()} chars after stripping.`,
                { id: t },
            );
            setCurating({ ck: item, preview: data });
        } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail) || "Fetch failed", { id: t });
        }
    };

    const recheckAll = async () => {
        if (!window.confirm("Re-check every common-knowledge source URL now? "
            + "This may take a few minutes and will email all active admins on detected changes.")) return;
        setRechecking(true);
        const t = toast.loading("Running source re-check…");
        try {
            const { data } = await api.post(
                `/admin/common-knowledge/_recheck-sources`,
                {},
                { timeout: 300000 },
            );
            const sent = data?.email?.sent || 0;
            toast.success(
                `Re-check complete · checked ${data.checked} · changed ${data.newly_flagged}`
                + (sent ? ` · emailed ${sent} admin(s)` : ""),
                { id: t },
            );
            load();
        } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail) || "Re-check failed", { id: t });
        } finally {
            setRechecking(false);
        }
    };

    return (
        <div className="px-8 py-8" data-testid="common-knowledge-page">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="label-xs text-[#7C3AED] flex items-center gap-2"><BookOpen className="w-3 h-3" /> Common Knowledge</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">Cross-tenant Knowledge Library</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        Regulatory standards, generic SOPs, prompt templates, and public references shared across every tenant.
                        AI agents can read this content as <span className="font-mono text-emerald-300">classification=common</span>.
                        <span className="block mt-1 text-[11px] text-white/40">No PHI, PII, or proprietary client information allowed here. Authoring requires <code className="text-white/70">admin.manage</code>.</span>
                    </p>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setEditor("new")} data-testid="ck-new" className="bg-[#7C3AED] hover:bg-[#7C3AED]/90 text-white px-3 py-2 rounded-md text-sm flex items-center gap-1.5">
                        <Plus className="w-4 h-4" /> New entry
                    </button>
                    <button onClick={recheckAll} disabled={rechecking} data-testid="ck-recheck-all" className="border border-amber-300/40 text-amber-200 px-3 py-2 rounded-md text-sm hover:bg-amber-300/10 disabled:opacity-50 flex items-center gap-1.5">
                        <Activity className={`w-4 h-4 ${rechecking ? "animate-pulse" : ""}`} />
                        {rechecking ? "Re-checking…" : "Re-check sources"}
                    </button>
                    <button onClick={load} data-testid="ck-refresh" className="border border-white/15 px-3 py-2 rounded-md text-sm hover:bg-white/5 flex items-center gap-1.5">
                        <RefreshCw className="w-4 h-4" /> Refresh
                    </button>
                </div>
            </div>

            {/* Kind cards */}
            <div className="grid grid-cols-4 gap-2 mb-5">
                {KIND_OPTIONS.map((k) => {
                    const meta = KIND_META[k];
                    const active = filters.kind === k;
                    return (
                        <button
                            key={k}
                            data-testid={`ck-kpi-${k}`}
                            onClick={() => { setFilters((p) => ({ ...p, kind: active ? "" : k })); setTimeout(load, 0); }}
                            className={`bg-[#0a0a0a] border rounded-md p-3 text-left transition-colors ${active ? "border-white/40" : "border-white/10 hover:bg-white/5"}`}
                        >
                            <div className="label-xs">{meta.label}</div>
                            <div className="font-display text-2xl mt-1">{counts[k] || 0}</div>
                        </button>
                    );
                })}
            </div>

            {/* Search */}
            <form onSubmit={(e) => { e.preventDefault(); load(); }} className="bg-[#0a0a0a] border border-white/10 rounded-md p-4 mb-5 flex gap-3">
                <input type="text" placeholder="Search title, content, summary…" value={filters.q}
                    onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
                    data-testid="ck-search"
                    className="flex-1 bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-white/30" />
                <button type="submit" data-testid="ck-apply" className="px-4 py-2 text-sm bg-[#7C3AED] text-white rounded-md flex items-center gap-2">
                    <Search className="w-4 h-4" /> Apply
                </button>
            </form>

            {/* List */}
            <div className="bg-[#0a0a0a] border border-white/10 rounded-md">
                {loading ? <div className="px-5 py-8 text-center text-xs text-white/40 font-mono">Loading…</div>
                    : items.length === 0 ? (
                        <div className="px-5 py-16 text-center">
                            <BookOpen className="w-10 h-10 text-white/20 mx-auto mb-3" strokeWidth={1.2} />
                            <div className="text-sm text-white/60">No entries match these filters</div>
                        </div>
                    ) : items.map((it, idx) => {
                        const meta = KIND_META[it.kind] || KIND_META.reference;
                        const needsRecuration = !!it.change_detected_at;
                        return (
                            <div key={it.id} data-testid={`ck-row-${idx}`} className={`px-5 py-4 border-t border-white/5 first:border-t-0 hover:bg-white/[0.02] ${needsRecuration ? "bg-amber-300/[0.03]" : ""}`}>
                                <div className="flex items-start gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider border rounded-sm ${meta.tone}`}>{meta.label}</span>
                                            <h3 className="font-display text-base tracking-tight">{it.title}</h3>
                                            {needsRecuration && (
                                                <span title={`Upstream changed on ${fmt(it.change_detected_at)} — re-curate to refresh the datalake.`} data-testid={`ck-needs-recurate-${idx}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded-sm border border-amber-400/40 bg-amber-400/10 text-amber-200">
                                                    <AlertTriangle className="w-3 h-3" /> Re-curation needed
                                                </span>
                                            )}
                                        </div>
                                        {it.summary && <div className="text-sm text-white/65 mt-0.5">{it.summary}</div>}
                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[11px] font-mono text-white/40">
                                            <span>slug: <code className="text-white/65">{it.slug}</code></span>
                                            <span>updated: {fmt(it.updated_at)}</span>
                                            {it.last_curated_at && <span>curated: {fmt(it.last_curated_at)} {it.last_curated_by ? `· ${it.last_curated_by}` : ""}</span>}
                                            {it.last_check_error && <span className="text-amber-400/80" title={it.last_check_error}>last-check: {it.last_check_error.slice(0, 32)}{it.last_check_error.length > 32 ? "…" : ""}</span>}
                                            {it.source_url && <a href={it.source_url} target="_blank" rel="noreferrer" className="text-sky-300 hover:text-sky-200 flex items-center gap-1"><ExternalLink className="w-3 h-3" /> source</a>}
                                            {(it.tags || []).slice(0, 5).map((t) => <span key={t} className="flex items-center gap-1 text-white/55"><Tag className="w-3 h-3" />{t}</span>)}
                                        </div>
                                    </div>
                                    <div className="flex gap-1">
                                        {it.source_url && (
                                            <button onClick={() => ingestSource(it)} title="Fetch source URL and open the curation editor" data-testid={`ck-ingest-${idx}`} className={`p-1.5 rounded text-white/70 ${needsRecuration ? "bg-amber-400/15 hover:bg-amber-400/25 text-amber-200" : "hover:bg-[#7C3AED]/20 hover:text-[#c7a6ff]"}`}>
                                                <Download className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                        <button onClick={() => setEditor(it)} title="Edit" data-testid={`ck-edit-${idx}`} className="p-1.5 rounded hover:bg-white/10 text-white/70"><Edit3 className="w-3.5 h-3.5" /></button>
                                        <button onClick={() => remove(it)} title="Delete" data-testid={`ck-delete-${idx}`} className="p-1.5 rounded hover:bg-red-500/20 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
            </div>

            {curating !== null && (
                <CurationModal
                    ck={curating.ck}
                    preview={curating.preview}
                    onClose={() => setCurating(null)}
                    onDone={() => { setCurating(null); load(); }}
                />
            )}

            {editor !== null && (
                <Editor
                    item={editor === "new" ? null : editor}
                    onClose={() => setEditor(null)}
                    onDone={() => { setEditor(null); load(); }}
                />
            )}
        </div>
    );
}

function Editor({ item, onClose, onDone }) {
    const isNew = !item;
    const [form, setForm] = useState(() => ({
        title: item?.title || "",
        kind: item?.kind || "regulatory",
        summary: item?.summary || "",
        content: item?.content || "",
        source_url: item?.source_url || "",
        tags: (item?.tags || []).join(", "),
    }));
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                title: form.title.trim(),
                kind: form.kind,
                summary: form.summary || null,
                content: form.content || "",
                source_url: form.source_url || null,
                tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
            };
            if (isNew) {
                const { data } = await api.post(`/admin/common-knowledge`, payload);
                // Backend mirrors the row into the datalake when content
                // is non-empty (text-only direct insert) — surface that
                // to the curator so they know it's already searchable.
                if (data?.datalake_item_id) {
                    toast.success("Created and inserted into datalake");
                } else {
                    toast.success("Created");
                }
            } else {
                const { data } = await api.patch(`/admin/common-knowledge/${item.id}`, payload);
                if (data?.datalake_item_id) {
                    toast.success("Updated · datalake row replaced");
                } else {
                    toast.success("Updated");
                }
            }
            onDone();
        } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e)=>{e.currentTarget._dn=e.target===e.currentTarget;}} onMouseUp={(e)=>{if(e.currentTarget._dn&&e.target===e.currentTarget)onClose();e.currentTarget._dn=false;}}>
            <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden bg-[#0a0a0a] border border-white/15 rounded-md flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="ck-editor">
                <div className="px-5 py-4 border-b border-white/10 flex items-center">
                    <div className="font-display text-lg tracking-tight">{isNew ? "New common-knowledge entry" : `Edit · ${item.title}`}</div>
                    <button onClick={onClose} className="ml-auto text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 overflow-auto space-y-3">
                    <Field label="Title" required value={form.title} onChange={(v) => setForm((p) => ({ ...p, title: v }))} testId="ck-title" />
                    <div>
                        <div className="label-xs mb-1.5">Kind</div>
                        <div className="flex gap-2 flex-wrap">
                            {KIND_OPTIONS.map((k) => (
                                <button key={k} type="button" data-testid={`ck-kind-${k}`}
                                    onClick={() => setForm((p) => ({ ...p, kind: k }))}
                                    className={`px-3 py-1.5 text-xs rounded-md border ${form.kind === k ? "border-[#7C3AED]/60 bg-[#7C3AED]/15 text-white" : "border-white/15 text-white/70 hover:bg-white/5"}`}>
                                    {KIND_META[k].label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <Field label="Summary" value={form.summary} onChange={(v) => setForm((p) => ({ ...p, summary: v }))} testId="ck-summary" />
                    <div>
                        <div className="label-xs mb-1.5">Content (markdown supported)</div>
                        <textarea value={form.content} onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))}
                            data-testid="ck-content" rows={10}
                            className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-white/30" />
                        <div className="text-[11px] text-white/40 mt-1.5">
                            Text-only entries — leave Source URL blank and just paste content here. The datalake mirror is created automatically on save, so AI agents pick it up immediately.
                        </div>
                    </div>
                    <Field label="Source URL" value={form.source_url} onChange={(v) => setForm((p) => ({ ...p, source_url: v }))} testId="ck-source" placeholder="https://…" />
                    <Field label="Tags (comma-separated)" value={form.tags} onChange={(v) => setForm((p) => ({ ...p, tags: v }))} testId="ck-tags" placeholder="hipaa, privacy" />
                </div>
                <div className="px-5 py-4 border-t border-white/10 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm border border-white/15 rounded-md hover:bg-white/5">Cancel</button>
                    <button onClick={save} disabled={saving || !form.title} data-testid="ck-save"
                        className="px-4 py-2 text-sm bg-[#7C3AED] text-white rounded-md hover:bg-[#7C3AED]/90 disabled:opacity-40">
                        {saving ? "Saving…" : (isNew ? "Create" : "Save")}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, required, testId, placeholder }) {
    return (
        <div>
            <div className="label-xs mb-1.5">{label}{required && <span className="text-[#7C3AED] ml-1">*</span>}</div>
            <input type="text" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} placeholder={placeholder || ""}
                className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-white/30" />
        </div>
    );
}

function CurationModal({ ck, preview, onClose, onDone }) {
    // The curator starts editing FROM the freshly-fetched preview text.
    // We keep the existing curated content one click away (the "Reset
    // to current" button) so they can fall back if their edit goes
    // wrong before pressing Save.
    const [curated, setCurated] = useState(preview?.preview_text || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!curated.trim()) { toast.error("Curated content cannot be empty"); return; }
        setSaving(true);
        try {
            await api.post(`/admin/common-knowledge/${ck.id}/curate`, {
                content: curated,
            });
            toast.success("Curated content saved and replicated to datalake");
            onDone();
        } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail) || "Curation failed");
        } finally {
            setSaving(false);
        }
    };

    const sizeKb = (preview?.bytes || 0) / 1024;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e)=>{e.currentTarget._dn=e.target===e.currentTarget;}} onMouseUp={(e)=>{if(e.currentTarget._dn&&e.target===e.currentTarget)onClose();e.currentTarget._dn=false;}}>
            <div className="w-full max-w-6xl max-h-[92vh] overflow-hidden bg-[#0a0a0a] border border-white/15 rounded-md flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="ck-curation-modal">
                <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
                    <div>
                        <div className="label-xs text-[#c7a6ff] flex items-center gap-2"><Download className="w-3 h-3" /> Curate ingested source</div>
                        <div className="font-display text-lg tracking-tight mt-0.5">{ck.title}</div>
                        <div className="text-[11px] font-mono text-white/40 mt-1">
                            {sizeKb.toFixed(1)} KB · {preview?.content_type || "?"} ·
                            HTTP <span className={preview?.status_code && preview.status_code < 400 ? "text-emerald-300" : "text-amber-300"}>{preview?.status_code || "?"}</span> ·
                            hash <code className="text-white/65">{(preview?.new_hash || "").slice(0, 12)}…</code>
                            {preview?.unchanged && <span className="ml-2 text-emerald-300/80">unchanged since last curation</span>}
                        </div>
                    </div>
                    <a href={ck.source_url} target="_blank" rel="noreferrer" className="ml-auto text-sky-300 hover:text-sky-200 flex items-center gap-1 text-xs"><ExternalLink className="w-3.5 h-3.5" /> open source</a>
                    <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="flex-1 overflow-hidden grid grid-cols-2 gap-0 min-h-0">
                    <div className="border-r border-white/10 flex flex-col min-h-0">
                        <div className="px-4 py-2 border-b border-white/10 text-[11px] uppercase tracking-wider text-white/50 font-mono flex items-center justify-between">
                            <span>Fetched source · read-only</span>
                            <span>{(preview?.preview_text || "").length.toLocaleString()} chars</span>
                        </div>
                        <pre className="flex-1 overflow-auto p-4 text-xs text-white/65 font-mono whitespace-pre-wrap leading-relaxed" data-testid="ck-curation-source">{preview?.preview_text || ""}</pre>
                    </div>
                    <div className="flex flex-col min-h-0">
                        <div className="px-4 py-2 border-b border-white/10 text-[11px] uppercase tracking-wider text-white/50 font-mono flex items-center justify-between">
                            <span>Curated · editable</span>
                            <div className="flex items-center gap-2 normal-case">
                                <button
                                    onClick={() => setCurated(preview?.preview_text || "")}
                                    className="text-[11px] text-white/60 hover:text-white"
                                    data-testid="ck-curation-reset-fetched"
                                    title="Replace your edits with the fresh fetch"
                                >Reset to fetched</button>
                                <span className="text-white/20">·</span>
                                <button
                                    onClick={() => setCurated(preview?.current_content || "")}
                                    className="text-[11px] text-white/60 hover:text-white"
                                    data-testid="ck-curation-reset-current"
                                    title="Restore the version currently in the datalake"
                                >Restore current</button>
                            </div>
                        </div>
                        <textarea
                            value={curated}
                            onChange={(e) => setCurated(e.target.value)}
                            data-testid="ck-curation-edit"
                            spellCheck={false}
                            className="flex-1 bg-[#050505] resize-none p-4 text-xs text-white/85 font-mono whitespace-pre-wrap leading-relaxed focus:outline-none"
                        />
                    </div>
                </div>
                <div className="px-5 py-4 border-t border-white/10 flex items-center gap-3">
                    <div className="text-[11px] text-white/40 font-mono">
                        Saving will replace the prior datalake entry for this knowledge.
                    </div>
                    <button onClick={onClose} className="ml-auto px-4 py-2 text-sm border border-white/15 rounded-md hover:bg-white/5">Cancel</button>
                    <button
                        onClick={save}
                        disabled={saving || !curated.trim()}
                        data-testid="ck-curation-save"
                        className="px-4 py-2 text-sm bg-[#7C3AED] text-white rounded-md hover:bg-[#7C3AED]/90 disabled:opacity-40"
                    >
                        {saving ? "Saving…" : "Approve & save"}
                    </button>
                </div>
            </div>
        </div>
    );
}
